import { API_BASE_URL } from '../config/api'
import { getAuthHeadersAsync } from '../utils/authUtils'

export interface Story {
  id: string
  title: string
  content: string
  excerpt?: string
  authorId: string
  authorName: string
  imageUrl?: string
  tags: string[]


  
  isPublished: boolean
  isPinned: boolean
  viewCount: number
  createdAt: string
  updatedAt: string
  author?: {
    firstName: string
    lastName: string
    userType: string
  }
}

export interface CreateStoryData {
  title: string
  content: string
  excerpt?: string
  imageUrl?: string
  tags?: string[]
  isPublished?: boolean
}

export interface UpdateStoryData {
  title?: string
  content?: string
  excerpt?: string
  imageUrl?: string
  tags?: string[]
  isPublished?: boolean
}

// Get all stories
export async function getStories(): Promise<Story[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/stories`)
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch stories')
    }
    
    return data.data
  } catch (error) {
    console.error('Error fetching stories:', error)
    return []
  }
}

// Get a specific story by ID
export async function getStory(id: string): Promise<Story | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/stories/${id}`)
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch story')
    }
    
    return data.data
  } catch (error) {
    console.error('Error fetching story:', error)
    return null
  }
}

// Create a new story
export async function createStory(storyData: CreateStoryData): Promise<Story | null> {
  try {
    const authHeaders = await getAuthHeadersAsync(true)
    if (!authHeaders.Authorization) {
      throw new Error('No authentication token found')
    }
    
    const response = await fetch(`${API_BASE_URL}/api/stories`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders
      },
      body: JSON.stringify(storyData)
    })
    
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to create story')
    }
    
    return data.data
  } catch (error) {
    console.error('Error creating story:', error)
    throw error
  }
}

// Update a story
export async function updateStory(storyData: UpdateStoryData & { id: string }): Promise<Story | null> {
  try {
    const authHeaders = await getAuthHeadersAsync(true)
    if (!authHeaders.Authorization) {
      throw new Error('No authentication token found')
    }
    
    const response = await fetch(`${API_BASE_URL}/api/stories/${storyData.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders
      },
      body: JSON.stringify(storyData)
    })
    
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to update story')
    }
    
    return data.data
  } catch (error) {
    console.error('Error updating story:', error)
    throw error
  }
}

// Delete a story
export async function deleteStory(id: string): Promise<boolean> {
  try {
    const authHeaders = await getAuthHeadersAsync(true)
    if (!authHeaders.Authorization) {
      throw new Error('No authentication token found')
    }
    
    const response = await fetch(`${API_BASE_URL}/api/stories/${id}`, {
      method: 'DELETE',
      headers: {
        ...authHeaders
      }
    })
    
    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.error || 'Failed to delete story')
    }
    
    return true
  } catch (error) {
    console.error('Error deleting story:', error)
    throw error
  }
}

// Pin/unpin a story
export async function toggleStoryPin(id: string): Promise<Story | null> {
  try {
    const authHeaders = await getAuthHeadersAsync(true)
    if (!authHeaders.Authorization) {
      throw new Error('No authentication token found')
    }
    
    const response = await fetch(`${API_BASE_URL}/api/stories/${id}/pin`, {
      method: 'PUT',
      headers: {
        ...authHeaders
      }
    })
    
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to toggle story pin')
    }
    
    return data.data
  } catch (error) {
    console.error('Error toggling story pin:', error)
    throw error
  }
}

// Notify about a story
export async function notifyStory(id: string): Promise<boolean> {
  try {
    const authHeaders = await getAuthHeadersAsync(true)
    if (!authHeaders.Authorization) {
      throw new Error('No authentication token found')
    }
    
    const response = await fetch(`${API_BASE_URL}/api/stories/${id}/notify`, {
      method: 'POST',
      headers: {
        ...authHeaders
      }
    })
    
    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.error || 'Failed to notify about story')
    }
    
    return true
  } catch (error) {
    console.error('Error notifying about story:', error)
    throw error
  }
}

// Get stories by current user
export async function getMyStories(): Promise<Story[]> {
  try {
    const authHeaders = await getAuthHeadersAsync(true)
    if (!authHeaders.Authorization) {
      throw new Error('No authentication token found')
    }
    
    const response = await fetch(`${API_BASE_URL}/api/stories/my`, {
      headers: {
        ...authHeaders
      }
    })
    
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch my stories')
    }
    
    return data.data
  } catch (error) {
    console.error('Error fetching my stories:', error)
    return []
  }
} 